import { useState, useCallback, useMemo } from 'react';

/**
 * Centralized loading state management hook
 * Provides consistent loading patterns across the app
 */
export const useLoadingState = () => {
  const [loadingStates, setLoadingStates] = useState({
    global: false,
    navigation: false,
    data: false,
    media: false,
    actions: {},
    timeouts: {}
  });

  // Set loading state for different types
  const setLoading = useCallback((type, isLoading, action = null, timeout = null) => {
    if (action) {
      // Action-specific loading
      setLoadingStates(prev => ({
        ...prev,
        actions: { ...prev.actions, [action]: isLoading }
      }));
    } else {
      // Global loading types
      setLoadingStates(prev => ({
        ...prev,
        [type]: isLoading
      }));
    }

    // Handle timeout for automatic loading state reset
    if (timeout && isLoading) {
      const timeoutId = setTimeout(() => {
        setLoading(type, false, action);
      }, timeout);

      setLoadingStates(prev => ({
        ...prev,
        timeouts: { ...prev.timeouts, [`${type}_${action || 'global'}`]: timeoutId }
      }));
    } else if (!isLoading && action) {
      // Clear timeout when loading stops
      const timeoutKey = `${type}_${action}`;
      if (loadingStates.timeouts[timeoutKey]) {
        clearTimeout(loadingStates.timeouts[timeoutKey]);
        setLoadingStates(prev => {
          const newTimeouts = { ...prev.timeouts };
          delete newTimeouts[timeoutKey];
          return { ...prev, timeouts: newTimeouts };
        });
      }
    }
  }, [loadingStates.timeouts]);

  // Check if any loading state is active
  const isAnyLoading = useMemo(() => {
    return loadingStates.global || 
           loadingStates.navigation || 
           loadingStates.data || 
           loadingStates.media ||
           Object.values(loadingStates.actions).some(Boolean);
  }, [loadingStates]);

  // Get loading state for specific type/action
  const getLoadingState = useCallback((type, action = null) => {
    if (action) {
      return loadingStates.actions[action] || false;
    }
    return loadingStates[type] || false;
  }, [loadingStates]);

  // Clear all loading states
  const clearAllLoading = useCallback(() => {
    // Clear all timeouts
    Object.values(loadingStates.timeouts).forEach(timeoutId => {
      clearTimeout(timeoutId);
    });

    setLoadingStates({
      global: false,
      navigation: false,
      data: false,
      media: false,
      actions: {},
      timeouts: {}
    });
  }, [loadingStates.timeouts]);

  // Loading state types with descriptions
  const LOADING_TYPES = {
    GLOBAL: 'global',           // App-wide loading (initial load, auth)
    NAVIGATION: 'navigation',   // Page transitions
    DATA: 'data',              // Data fetching (API calls, Firestore)
    MEDIA: 'media',           // Media loading (images, audio, video)
    ACTION: 'action'           // User actions (button clicks, form submissions)
  };

  return {
    loadingStates,
    setLoading,
    isAnyLoading,
    getLoadingState,
    clearAllLoading,
    LOADING_TYPES
  };
};




